import { useState, useEffect } from 'react';

interface TimeLockedState {
  isFullMoon: boolean;
  isWeekend: boolean;
  is317AM: boolean;
  currentHour: number;
  isNightTime: boolean;
  isMidnight: boolean;
}

// Approximate full moon calculation
const isFullMoonToday = (): boolean => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  
  // Lunar cycle is approximately 29.53 days
  // Known full moon: January 25, 2024
  const knownFullMoon = new Date(2024, 0, 25);
  const diffDays = Math.floor((now.getTime() - knownFullMoon.getTime()) / (1000 * 60 * 60 * 24));
  const lunarCycle = 29.53;
  const daysIntoLunarCycle = diffDays % lunarCycle;
  
  // Full moon is approximately at day 14-15 of the cycle
  return daysIntoLunarCycle >= 13 && daysIntoLunarCycle <= 16;
};

const isWeekendNow = (): boolean => {
  const day = new Date().getDay();
  return day === 0 || day === 6; // Sunday or Saturday
};

const is317AMNow = (): boolean => {
  const now = new Date();
  return now.getHours() === 3 && now.getMinutes() >= 15 && now.getMinutes() <= 19;
};

export const useTimeLocked = () => {
  const [state, setState] = useState<TimeLockedState>(() => {
    const now = new Date();
    const hour = now.getHours();
    return {
      isFullMoon: isFullMoonToday(),
      isWeekend: isWeekendNow(),
      is317AM: is317AMNow(),
      currentHour: hour,
      isNightTime: hour >= 22 || hour < 6,
      isMidnight: hour >= 0 && hour < 1,
    };
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const hour = now.getHours();
      setState({
        isFullMoon: isFullMoonToday(),
        isWeekend: isWeekendNow(),
        is317AM: is317AMNow(),
        currentHour: hour,
        isNightTime: hour >= 22 || hour < 6,
        isMidnight: hour >= 0 && hour < 1,
      });
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  return state;
};

export const getTimeLockedRoomAvailability = (roomId: string, state: TimeLockedState) => {
  switch (roomId) {
    case 'full-moon-room':
      return { available: state.isFullMoon, reason: 'Only available during full moon' };
    case 'weekend-room':
      return { available: state.isWeekend, reason: 'Only available on weekends' };
    case '317am-room':
      return { available: state.is317AM, reason: 'Only available at 3:17 AM' };
    case 'midnight-room':
      return { available: state.isMidnight, reason: 'Only available at midnight' };
    case 'night-room':
      return { available: state.isNightTime, reason: 'Only available after 10 PM' };
    default:
      return { available: true, reason: '' };
  }
};